import { useState } from "react";
import toast from "react-hot-toast";
import { Scale, Plus, CalendarClock } from "lucide-react";
import {
  useLegalCases,
  useUpdateLegalCase,
  LEGAL_CASE_STATUS_LABEL,
  LegalCaseStatus,
  hasUpcomingHearing,
} from "@/api/legal";
import { getApiErrorMessage } from "@/api/client";
import { useAuthStore } from "@/stores/authStore";
import CreateLegalCaseModal from "@/pages/legal/CreateLegalCaseModal";

const STATUS_STYLE: Record<LegalCaseStatus, string> = {
  ACTIVO: "bg-blue-100 text-blue-700",
  SUSPENDIDO: "bg-amber-100 text-amber-700",
  ARCHIVADO: "bg-gray-100 text-gray-400",
  TERMINADO: "bg-green-100 text-green-700",
};

const STATUS_OPTIONS = Object.keys(LEGAL_CASE_STATUS_LABEL) as LegalCaseStatus[];

export default function LegalCasesSection({ clientId }: { clientId: string }) {
  const hasPermission = useAuthStore((s) => s.hasPermission);
  const { data, isLoading } = useLegalCases({ clientId, pageSize: 50 });
  const updateLegalCase = useUpdateLegalCase();
  const [showCreate, setShowCreate] = useState(false);

  const handleStatusChange = async (id: string, status: LegalCaseStatus) => {
    try {
      await updateLegalCase.mutateAsync({ id, status });
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    }
  };

  return (
    <div className="card p-6">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-gray-900">Jurídico</h2>
        {hasPermission("legal.create") && (
          <button
            type="button"
            className="inline-flex items-center gap-1 text-xs font-medium text-brand-600 hover:text-brand-800"
            onClick={() => setShowCreate(true)}
          >
            <Plus size={14} /> Nuevo caso
          </button>
        )}
      </div>

      <div className="mt-4 space-y-2">
        {isLoading && <p className="text-sm text-gray-400">Cargando casos jurídicos...</p>}
        {!isLoading && (!data?.data || data.data.length === 0) && (
          <p className="text-sm text-gray-400">Este cliente aún no tiene casos jurídicos registrados.</p>
        )}
        {data?.data.map((legalCase) => (
          <div key={legalCase.id} className="flex items-center justify-between rounded-md border border-gray-100 p-3">
            <div className="flex items-center gap-3 overflow-hidden">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-brand-50 text-brand-600">
                <Scale size={15} />
              </div>
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-gray-900">{legalCase.title}</p>
                <p className="text-xs text-gray-500">
                  {legalCase.court || "Sin juzgado/entidad"}
                  {legalCase.nextHearingDate && (
                    <span className={hasUpcomingHearing(legalCase) ? "ml-1 inline-flex items-center gap-1 text-blue-600" : "ml-1"}>
                      {hasUpcomingHearing(legalCase) && <CalendarClock size={10} className="inline" />} · Audiencia {legalCase.nextHearingDate}
                    </span>
                  )}
                </p>
              </div>
            </div>
            {hasPermission("legal.update") ? (
              <select
                className={`shrink-0 rounded-full border-0 px-2 py-0.5 text-xs font-medium ${STATUS_STYLE[legalCase.status]}`}
                value={legalCase.status}
                onChange={(e) => handleStatusChange(legalCase.id, e.target.value as LegalCaseStatus)}
              >
                {STATUS_OPTIONS.map((s) => (
                  <option key={s} value={s}>
                    {LEGAL_CASE_STATUS_LABEL[s]}
                  </option>
                ))}
              </select>
            ) : (
              <span className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_STYLE[legalCase.status]}`}>
                {LEGAL_CASE_STATUS_LABEL[legalCase.status]}
              </span>
            )}
          </div>
        ))}
      </div>

      {showCreate && <CreateLegalCaseModal clientId={clientId} onClose={() => setShowCreate(false)} />}
    </div>
  );
}
